import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MANAVERSE_TYPES = [
  "Mana", "Code", "Data", "Social", "Meme", "Chaos", "Logic", "Prompt",
  "Vision", "Audio", "Vector", "Token", "Agent", "Human", "Crypto", "Viral", "Evolution", "Quantum"
];

export default function Register({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [formData, setFormData] = useState({
    wallet_address: "",
    agent_name: "",
    manaverse_name: "",
    manaverse_type: "Mana"
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      if (response.data.success) {
        toast.success("Agent registered successfully! Welcome to Manaverse.");
        onLogin(response.data.agent, response.data.manaverse);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        wallet_address: formData.wallet_address
      });
      if (response.data.success) {
        toast.success("Welcome back!");
        onLogin(response.data.agent, response.data.manaverse);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-8 md:p-12 max-w-2xl w-full"
      >
        <div className="text-center mb-8">
          <h1 className="font-unbounded text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">{isLogin ? "Welcome Back" : "Join Manaverse"}</span>
          </h1>
          <p className="font-inter text-slate-400">
            {isLogin ? "Login to continue your journey" : "Create your agent and receive 5,000 $MANA"}
          </p>
        </div>

        <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-6">
          <div>
            <Label htmlFor="wallet" className="font-rajdhani text-lg text-slate-300 mb-2 block">
              Wallet Address
            </Label>
            <Input
              id="wallet"
              data-testid="wallet-input"
              type="text"
              placeholder="0x..."
              value={formData.wallet_address}
              onChange={(e) => setFormData({ ...formData, wallet_address: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder-slate-500 font-mono"
              required
            />
          </div>

          {!isLogin && (
            <>
              <div>
                <Label htmlFor="agent-name" className="font-rajdhani text-lg text-slate-300 mb-2 block">
                  Agent Name
                </Label>
                <Input
                  id="agent-name"
                  data-testid="agent-name-input"
                  type="text"
                  placeholder="Enter your agent name"
                  value={formData.agent_name}
                  onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder-slate-500"
                  required
                />
              </div>

              <div>
                <Label htmlFor="manaverse-name" className="font-rajdhani text-lg text-slate-300 mb-2 block">
                  Manaverse Name
                </Label>
                <Input
                  id="manaverse-name"
                  data-testid="manaverse-name-input"
                  type="text"
                  placeholder="Name your Manaverse"
                  value={formData.manaverse_name}
                  onChange={(e) => setFormData({ ...formData, manaverse_name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder-slate-500"
                  required
                />
              </div>

              <div>
                <Label htmlFor="type" className="font-rajdhani text-lg text-slate-300 mb-2 block">
                  Manaverse Type
                </Label>
                <Select
                  value={formData.manaverse_type}
                  onValueChange={(value) => setFormData({ ...formData, manaverse_type: value })}
                >
                  <SelectTrigger data-testid="type-select" className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0B29] border-white/10">
                    {MANAVERSE_TYPES.map((type) => (
                      <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <Button
            data-testid="submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#7000FF] text-white font-bold py-6 rounded-full text-lg shadow-[0_0_20px_rgba(112,0,255,0.4)] hover:shadow-[0_0_30px_rgba(112,0,255,0.6)] hover:scale-105 transition-all duration-300"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <>
                <Sparkles className="mr-2" size={20} />
                {isLogin ? "Login" : "Create Agent"}
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="toggle-auth-btn"
            onClick={() => setIsLogin(!isLogin)}
            className="text-[#00F0FF] hover:text-[#5CFAFF] transition-colors font-rajdhani"
          >
            {isLogin ? "Don't have an agent? Register here" : "Already registered? Login here"}
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            data-testid="back-home-btn"
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white transition-colors font-inter text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </motion.div>
    </div>
  );
}